import {
  Badge,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import {
  CSSProperties,
  Children,
  FC,
  ReactElement,
  ReactNode,
  useState,
  forwardRef,
  Ref,
  isValidElement,
  useMemo,
  createContext,
  useContext,
  useRef,
} from 'react';
import { WbSearch } from '../WbSearch';
import { WbButton } from '../WbButton';
import { Dialog } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { Typography } from '@material-ui/core';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';
import clsx from 'clsx';
import pluralize from 'pluralize';

// Context for filter registration
interface FilterRegistryContextType {
  registerFilter: (id: string, hasValue: boolean) => void;
  unregisterFilter: (id: string) => void;
}

const FilterRegistryContext = createContext<FilterRegistryContextType | null>(
  null,
);

export const useFilterRegistry = () => {
  return useContext(FilterRegistryContext);
};

interface SearchProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

interface SelectedProps {
  selectedLabel?: string;
  selectedCount: number;
  onClearSelection: () => void;
}

interface WbResponsiveTableToolbarProps {
  className?: string;
  style?: CSSProperties;
  title?: string;
  children?: ReactNode;
  searchProps?: SearchProps;
  onClear?: () => void;
  clearButtonText?: string;
  selectedProps?: SelectedProps;
  marginBottom?: boolean;
  breakpoint?: Breakpoint;
}

interface FiltersProps {
  children: ReactNode;
}

interface ActionsProps {
  children: ReactNode;
}

type UseStylesProps = {
  breakpoint?: Breakpoint;
};

const useStyles = makeStyles<Theme, UseStylesProps>(
  (theme: Theme) => ({
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      containerType: 'inline-size',
    },
    toolbarMarginBottom: {
      marginBottom: theme.spacing(1),
    },
    desktopContent: ({ breakpoint = 'md' }) => ({
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap-reverse',
      columnGap: theme.spacing(1.5),
      rowGap: theme.spacing(0.75),
      width: '100%',
      [`@container (max-width: ${theme.breakpoints.values[breakpoint]}px)`]: {
        display: 'none',
      },
    }),
    filtersSection: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: theme.spacing(1.5),
      rowGap: theme.spacing(0.75),
    },
    actionsSection: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginLeft: 'auto',
    },
    clearSection: {
      flex: 1,
      height: '36px',
      minWidth: '100px',
      marginLeft: '20px',
      justifyContent: 'flex-end',
      display: 'flex',
      alignItems: 'center',
    },
    selectionSection: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    mobileActionsSection: () => ({
      display: 'flex',
      gap: theme.spacing(1),
      [`@container (max-width: ${theme.breakpoints.values.w_xs}px)`]: {
        order: 1,
        flexDirection: 'column',
        display: 'grid',
        gridTemplateColumns: '1fr',
      },
    }),
    mobileLayout: ({ breakpoint = 'md' }) => ({
      display: 'none',
      [`@container (max-width: ${theme.breakpoints.values[breakpoint]}px)`]: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap-reverse',
        gap: theme.spacing(1),
        width: '100%',
        justifyContent: 'space-between',
      },
      [`@container (max-width: ${theme.breakpoints.values.w_xs}px)`]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        '& > div:first-child': {
          order: 2,
        },
      },
    }),

    mobileFiltersButton: {
      borderRadius: theme.spacing(0.5),
      width: '36px',
      height: '36px',
      backgroundColor: theme.palette.white,
    },
    dialogHeader: {
      backgroundColor: theme.palette.grey[50],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    dialogTitle: {
      color: theme.palette.secondary.dark,
    },
    dialogContent: {
      padding: theme.spacing(1.5, 1.5),
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows:
        'max-content max-content max-content max-content max-content max-content max-content max-content',
      gap: theme.spacing(1.5),
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
      },
    },
    dialogFooter: {
      padding: theme.spacing(1, 1.5),
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
      },
    },
    dialogClearButton: {
      marginTop: theme.spacing(2),
      alignSelf: 'flex-start',
    },

    filler: {
      marginLeft: 'auto',
    },
  }),
  { name: 'WbResponsiveTableToolbar' },
);

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Components
const Filters: FC<FiltersProps> = ({ children }) => {
  return <>{children}</>;
};

const Actions: FC<ActionsProps> = ({ children }) => {
  return <>{children}</>;
};

const Filler: FC = () => {
  const classes = useStyles({});
  return <div className={classes.filler} />;
};

const isFiltersComponent = (
  child: ReactNode,
): child is ReactElement<FiltersProps> => {
  return isValidElement(child) && child.type === Filters;
};

const isActionsComponent = (
  child: ReactNode,
): child is ReactElement<ActionsProps> => {
  return isValidElement(child) && child.type === Actions;
};

interface MobileFiltersWrapperProps {
  children: ReactNode;
  title: string;
  onClear?: () => void;
  clearButtonText?: string;
}

const MobileFiltersWrapper: FC<MobileFiltersWrapperProps> = ({
  children,
  title,
  onClear,
  clearButtonText,
}) => {
  const classes = useStyles({});
  const [open, setOpen] = useState(false);
  const activeFiltersRef = useRef<Map<string, boolean>>(new Map());
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const hasFilters = Children.count(children) > 0;

  // Context value for filter registration
  const filterRegistry = useMemo<FilterRegistryContextType>(
    () => ({
      registerFilter: (id: string, hasValue: boolean) => {
        activeFiltersRef.current.set(id, hasValue);
        setActiveFilterCount(
          Array.from(activeFiltersRef.current.values()).filter(Boolean).length,
        );
      },
      unregisterFilter: (id: string) => {
        activeFiltersRef.current.delete(id);
        setActiveFilterCount(
          Array.from(activeFiltersRef.current.values()).filter(Boolean).length,
        );
      },
    }),
    [],
  );

  if (!hasFilters) return null;

  return (
    <FilterRegistryContext.Provider value={filterRegistry}>
      <Badge badgeContent={activeFilterCount} color="primary">
        <WbButton
          aria-label="open filters"
          onClick={handleClickOpen}
          icon={<FilterListIcon />}
          color="default"
          variant="outlined"
          className={classes.mobileFiltersButton}
        />
      </Badge>
      <Dialog
        PaperProps={{ 'aria-label': 'filters dialog' }}
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <DialogTitle disableTypography classes={{ root: classes.dialogHeader }}>
          <Typography
            variant="subtitle1"
            classes={{ root: classes.dialogTitle }}
          >
            {title}
          </Typography>
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          {children}
        </DialogContent>
        <DialogActions classes={{ root: classes.dialogFooter }}>
          {onClear && hasFilters && (
            <WbButton
              variant="outlined"
              size="small"
              color="default"
              onClick={onClear}
              label={clearButtonText || 'Clear filters'}
            />
          )}
          <WbButton
            size="small"
            variant="contained"
            color="primary"
            onClick={handleClose}
            label="Close"
          />
        </DialogActions>
      </Dialog>
    </FilterRegistryContext.Provider>
  );
};

export const WbResponsiveTableToolbar: FC<WbResponsiveTableToolbarProps> & {
  Filters: FC<FiltersProps>;
  Actions: FC<ActionsProps>;
  Filler: FC;
} = ({
  className,
  style,
  title = 'Filters',
  children = [],
  searchProps,
  onClear,
  clearButtonText,
  selectedProps,
  marginBottom,
  breakpoint = 'sm',
}) => {
  const classes = useStyles({ breakpoint });
  const theme = useTheme();

  const childrenArray = Children.toArray(children);
  const filtersChild = childrenArray.find(isFiltersComponent);
  const actionsChild = childrenArray.find(isActionsComponent);

  // If there are no compound components, simply render the children
  const hasCompoundComponents = filtersChild || actionsChild;

  if (!hasCompoundComponents) {
    return (
      <Box
        className={clsx(
          classes.toolbar,
          marginBottom && classes.toolbarMarginBottom,
          className,
        )}
        style={style}
      >
        {searchProps && (
          <WbSearch
            fullWidth={false}
            value={searchProps.value}
            onChange={searchProps.onChange}
            debounced
            style={{
              height: '36px',
            }}
          />
        )}
        {children}
      </Box>
    );
  }

  const filtersContent = filtersChild ? filtersChild.props.children : null;
  const actionsContent = actionsChild ? actionsChild.props.children : null;

  const hasFilters = Children.count(filtersContent) > 0;
  const hasActions = Children.count(actionsContent) > 0;

  const selectedLabel = selectedProps?.selectedLabel ?? 'item';

  const selectionNode = (
    <Box className={classes.selectionSection}>
      <Typography
        role="status"
        variant="body2"
        style={{ fontWeight: 'medium' }}
      >
        {`${selectedProps?.selectedCount} ${pluralize(
          selectedLabel,
          selectedProps?.selectedCount,
        )} selected`}
      </Typography>
      <WbButton
        variant="text"
        label="Clear selection"
        onClick={() => selectedProps?.onClearSelection()}
      />
    </Box>
  );

  const searchNode = searchProps && (
    <WbSearch
      fullWidth={false}
      value={searchProps.value}
      onChange={searchProps.onChange}
      debounced
      style={{
        height: '36px',
        minWidth: 'auto',
        flex: 1,
      }}
    />
  );

  return (
    <Box
      className={clsx(
        classes.toolbar,
        marginBottom && classes.toolbarMarginBottom,
        className,
      )}
      style={style}
    >
      {/* Desktop layout */}
      <Box className={classes.desktopContent}>
        <Box className={classes.filtersSection}>
          {searchNode}
          {filtersContent}
        </Box>
        <Box className={classes.actionsSection}>
          {onClear && hasFilters && (
            <Box className={classes.clearSection}>
              <WbButton
                variant="outlined"
                size="small"
                color="primary"
                label={clearButtonText || 'Clear filters'}
                onClick={onClear}
              />
            </Box>
          )}
          {hasActions && actionsContent}
        </Box>
      </Box>

      <Box className={classes.mobileLayout}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          {searchNode}
          {hasFilters && (
            <MobileFiltersWrapper
              title={title}
              onClear={onClear}
              clearButtonText={clearButtonText}
            >
              {filtersContent}
            </MobileFiltersWrapper>
          )}
        </Box>
        {hasActions && (
          <Box className={classes.mobileActionsSection}>{actionsContent}</Box>
        )}
      </Box>
      {selectedProps && selectedProps.selectedCount > 0 && selectionNode}
    </Box>
  );
};

// Attach compound components
WbResponsiveTableToolbar.Filters = Filters;
WbResponsiveTableToolbar.Actions = Actions;
WbResponsiveTableToolbar.Filler = Filler;
